import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../lib/i18n";
import { http, errText, AFFILIATE_URL } from "../../lib/api";
import { Reveal, Kicker, SectionTitle } from "./Reveal";
import { Checkbox } from "../ui/checkbox";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", address: "", address2: "", city: "", postal_code: "", country: "France", notes: "" };
const REQUIRED = ["first_name", "last_name", "email", "address", "city", "postal_code"];
const KEY = "mareia_registration";

const Field = ({ name, label, value, onChange, type = "text", span = "" }) => (
  <label className={`block ${span}`}>
    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#3F5F68]">{label}{REQUIRED.includes(name) && " *"}</span>
    <input type={type} name={name} value={value} onChange={onChange} data-testid={`input-${name.replace("_", "-")}`}
      className="w-full rounded-xl border border-[#073B4C]/15 bg-white px-4 py-3 text-[#073B4C] outline-none transition-[border-color,box-shadow] focus:border-[#0A7189] focus:ring-2 focus:ring-[#0A7189]/20" />
  </label>
);

const Unlocked = ({ reg, onReset }) => {
  const { t } = useLang();
  const click = () => { http.post(`/registrations/${reg.id}/click`).catch(() => {}); };
  return (
    <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
      className="rounded-3xl bg-[#073B4C] p-8 text-[#FBF7EF] md:p-10" data-testid="order-success">
      <CheckCircle2 className="h-10 w-10 text-[#7ED9D2]" />
      <h3 className="mt-5 font-display text-4xl leading-tight">{t.order.successTitle.replace("{name}", reg.first_name)}</h3>
      <p className="mt-3 text-[#FBF7EF]/80">{t.order.successBody}</p>
      <div className="mt-8 flex items-center gap-5 rounded-2xl bg-[#FBF7EF]/5 p-4">
        <img src="/img/front.png" alt="" className="h-20 w-20 object-contain" />
        <div>
          <p className="text-sm">{t.order.product}</p>
          <p className="font-display text-2xl text-[#7ED9D2]">{t.hero.price}</p>
        </div>
      </div>
      <a href={AFFILIATE_URL} target="_blank" rel="noopener noreferrer sponsored" onClick={click} data-testid="affiliate-buy-now-link"
        className="group mt-8 flex items-center justify-between rounded-full bg-[#7ED9D2] px-7 py-4 font-semibold text-[#073B4C] transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-[#A6E8E2]">
        {t.order.buy}
        <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
      </a>
      <button onClick={onReset} className="mt-5 text-sm text-[#FBF7EF]/70 underline-offset-4 hover:underline" data-testid="order-reset-btn">{t.order.reset}</button>
    </motion.div>
  );
};

export const OrderForm = () => {
  const { t, lang } = useLang();
  const [f, setF] = useState(EMPTY);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reg, setReg] = useState(() => { try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; } });
  const on = (e) => setF((p) => ({ ...p, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent || REQUIRED.some((k) => !f[k].trim())) { toast.error(t.order.required); return; }
    setBusy(true);
    try {
      const { data } = await http.post("/registrations", { ...f, lang });
      const r = { id: data?.id, first_name: data?.first_name ?? f.first_name };
      localStorage.setItem(KEY, JSON.stringify(r));
      setReg(r);
    } catch (err) { toast.error(errText(err)); }
    setBusy(false);
  };
  const reset = () => { localStorage.removeItem(KEY); setReg(null); setF(EMPTY); setConsent(false); };
  const F = (n, extra = {}) => <Field name={n} label={t.order.fields[n]} value={f[n]} onChange={on} {...extra} />;

  return (
    <section id="order" className="bg-[#FBF7EF] py-24 md:py-32" data-testid="order-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal><Kicker>{t.order.kicker}</Kicker></Reveal>
          <Reveal delay={0.05}><SectionTitle className="mt-6">{t.order.title}</SectionTitle></Reveal>
          <Reveal delay={0.1}><p className="mt-6 leading-relaxed text-[#3F5F68]" data-testid="order-explainer">{t.order.body}</p></Reveal>
          <Reveal delay={0.15}>
            <ol className="mt-10 space-y-4">
              {t.order.steps.map((s, i) => (
                <li key={s} className="flex items-center gap-4">
                  <span className={`flex h-10 w-10 items-center justify-center rounded-full font-mono-x text-sm ${i === 0 || (reg && i === 1) ? "bg-[#0A7189] text-[#FBF7EF]" : "border border-[#073B4C]/25"}`}>{i + 1}</span>
                  <span className="font-display text-2xl">{s}</span>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <AnimatePresence initial={false}>
            {reg ? <Unlocked key="ok" reg={reg} onReset={reset} /> : (
              <motion.form key="form" onSubmit={submit} exit={{ opacity: 0, y: -10 }} noValidate
                className="grid gap-4 rounded-3xl border border-[#073B4C]/10 bg-white/70 p-6 shadow-[0_30px_80px_-40px_rgba(11,28,34,0.35)] backdrop-blur md:grid-cols-2 md:p-9" data-testid="registration-form">
                {F("first_name")}{F("last_name")}
                {F("email", { type: "email" })}{F("phone", { type: "tel" })}
                {F("address", { span: "md:col-span-2" })}
                {F("address2", { span: "md:col-span-2" })}
                {F("postal_code")}{F("city")}
                <label className="block md:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#3F5F68]">{t.order.fields.country} *</span>
                  <div className="grid grid-cols-3 gap-2" data-testid="input-country-select">
                    {Object.entries(t.order.countries).map(([v, l]) => (
                      <button type="button" key={v} onClick={() => setF((p) => ({ ...p, country: v }))} data-testid={`country-${v === "France" ? "fr" : v === "Autre" ? "other" : "pf"}`}
                        className={`rounded-xl border px-3 py-3 text-sm transition-colors ${f.country === v ? "border-[#0A7189] bg-[#0A7189] text-[#FBF7EF]" : "border-[#073B4C]/15 bg-white hover:border-[#0A7189]"}`}>{l}</button>
                    ))}
                  </div>
                </label>
                <label className="block md:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#3F5F68]">{t.order.fields.notes}</span>
                  <textarea name="notes" value={f.notes} onChange={on} rows={2} data-testid="input-notes"
                    className="w-full rounded-xl border border-[#073B4C]/15 bg-white px-4 py-3 outline-none focus:border-[#0A7189] focus:ring-2 focus:ring-[#0A7189]/20" />
                </label>
                <label className="flex items-start gap-3 text-sm text-[#3F5F68] md:col-span-2">
                  <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" data-testid="input-consent" />
                  {t.order.consent}
                </label>
                <button type="submit" disabled={busy} data-testid="registration-submit"
                  className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#073B4C] px-7 py-4 font-semibold text-[#FBF7EF] transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-[#0A7189] disabled:opacity-60 md:col-span-2">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}{busy ? t.order.sending : t.order.submit}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
